import React, { useState, useEffect, useRef } from "react";

import { 
  Campaign, 
  CampaignNPC,
  Character, 
} from "@dndModels";

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import EditableLabel from "@components/input/strings/EditableLabel";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FlexBox from "@components/layout/FlexBox";
import VendorServicesInput from "@dndComponents/campaign/npcs/VendorServicesInput";
import VendorInventoryInput from "@dndComponents/campaign/npcs/VendorInventoryInput";
import StringBox from "@components/input/strings/StringBox";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import CharacterSheetParent from "@dndComponents/character/CharacterSheetParent";

interface Props {
  campaign: Campaign;
  npc: CampaignNPC;
  onDelete: () => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CampaignNPCInput({ campaign, npc, onDelete }: Props) {
  const [creating_char_sheet, setCreatingCharSheet] = useState(false);
  const [view_char_sheet, setViewCharSheet] = useState(false);
  const [reloading, setReloading] = useState(false);
  // const [processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const [open, setOpen] = useState(npc.name.length === 0);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        full_page
        onClose={() => { setOpen(false); setViewCharSheet(false); }}>
        { renderInputControls() }
      </ModalOrDrawer>
    )
  }

  const renderInputControls = () => {
    if (view_char_sheet && npc.char_sheet) {
      return (
        <FlexBox direction="column">
          <FlexBox>
            <DandDButtonBox
              name="Back"
              onClick={() => setViewCharSheet(false) }
            />
          </FlexBox>
          <FlexBox>
            <CharacterSheetParent
              id={npc.char_id}
              char={npc.char_sheet}
              campaign={campaign}
            />
          </FlexBox>
        </FlexBox>
      );
    } else {
      return (
        <FlexBox direction="column">
          <FlexBox direction="row">
            <EditableLabel
              name="Name"
              value={npc.name}
              fontSize={24}
              onChange={ async (changed: string) => {
                npc.name = changed;
                api.upsertObject("campaign_npc", npc);
                setPing(!ping);
              }}
            />
          </FlexBox>
          <FlexBox>
            <StringBox
              name="Appearance"
              value={npc.appearance}
              multiline
              onChange={(changed: string) => {
                npc.appearance = changed;
                api.upsertObject("campaign_npc", npc);
                setPing(!ping);
              }}
            />
          </FlexBox>
          <FlexBox>
            <StringBox
              name="Voice"
              value={npc.voice}
              multiline
              onChange={(changed: string) => {
                npc.voice = changed;
                api.upsertObject("campaign_npc", npc);
                setPing(!ping);
              }}
            />
          </FlexBox>
          <FlexBox>
            <StringBox
              name="Details"
              value={npc.description}
              multiline
              onChange={(changed: string) => {
                npc.description = changed;
                api.upsertObject("campaign_npc", npc);
                setPing(!ping);
              }}
            />
          </FlexBox>
          <FlexBox>
            <StringBox
              name="Will Buy"
              value={npc.will_buy}
              multiline
              onChange={(changed: string) => {
                npc.will_buy = changed;
                api.upsertObject("campaign_npc", npc);
                setPing(!ping);
              }}
            />
          </FlexBox>
          <FlexBox direction="row">
            <VendorServicesInput
              campaign={campaign}
              npc={npc}
            />
            <VendorInventoryInput
              campaign={campaign}
              npc={npc}
            />
            { renderCharSheetButton() }
          </FlexBox>
        </FlexBox>
      );
    }
  }

  const renderCharSheetButton = () => {
    const className = creating_char_sheet ? "header centered gray" : "header centered clickable"
    return (
      <FlexBox>
        <div className="padded">
          <FlexBox className={cx(classes.root, className)} 
            onClick={ async () => { 
              // If it has a char sheet, set it to view it. 
              // If not, create one, then view it.
              if (!npc.char_sheet) {
                setCreatingCharSheet(true);
                
                const new_char = new Character();
                new_char.name = npc.name;
                connect_util.connectCampaign2(new_char, campaign);
                new_char.last_campaign_update = campaign.last_updated;
                await char_util.enforceOptions(new_char, campaign);
                const res = await api.createCharacter(new_char);
                if (res && res.body.id) {
                  npc.char_id = new_char._id;
                  npc.char_sheet = new_char;
                  await api.upsertObject("campaign_npc", npc);
                } else {
                  console.log(res);
                }
                setCreatingCharSheet(false);
              }
              setViewCharSheet(true);
            }}>
            { creating_char_sheet ? "Creating Character Sheet" : "Character Sheet" }
          </FlexBox>
        </div>
      </FlexBox>
    );
  }

  return (
    <div className="padded">
      <FlexBox direction="column" className={cx(classes.root)}>
        <FlexBox direction="row">
          <FlexBox className="header padded clickable" onClick={() => {
            setOpen(true); 
          }}>{ npc.name }</FlexBox>
          <FlexBox xs_expand={0}>
            <div style={{ float: "right"}}>
              <DeleteButton
                no_fab
                obj_name={npc.name}
                onClick={onDelete}
              />
            </div>
          </FlexBox>
        </FlexBox>
        { npc.char_sheet &&
          <FlexBox className="header padded clickable"
            onClick={() => { 
              setOpen(true); 
              setViewCharSheet(true);
            }}>
            Character Sheet
          </FlexBox>
        }
      </FlexBox>
      { renderExtras() }
    </div>
  );
}
